import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import {
  StatMiniCard,
  DataTable,
  StatusBadge,
  PointsValue,
} from "../../components/Common/PageWidgets";
import GameActivityDetailModal from "../../components/Common/GameActivityDetailModal";
import EmptyState from "../../components/Common/EmptyState";

//Import API
import endpoints from "../../api/endpoints";
import { get } from "../../api/axios";

//Utils
import { extractErrorMessage } from "../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";


import gameControllerIcon from "../../assets/images/support/game.svg";
import gameBlue from "../../assets/images/support/game-blue.svg";
import gameGreen from "../../assets/images/support/game-green.svg";
import gameRed from "../../assets/images/support/game-red.svg";
import eyeIcon from "../../assets/images/support/eye.svg";
import chevronDownIcon from "../../assets/images/support/chevron-down.svg";

/* -------------------------------------------------------------------------
 * GamesPage — stat summary, active challenge cards, and full games history.
 * ---------------------------------------------------------------------- */

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "Unknown";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit'
  });
  return `${datePart} ${timePart}`;
};

const formatRangeStart = (dateString, includeYear) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' } : {})
  });
};

const formatWeekRange = (startValue, endValue) => {
  if (!startValue || !endValue) return "";
  const start = new Date(startValue);
  const end = new Date(endValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";

  const includeStartYear = start.getFullYear() !== end.getFullYear();
  return `${formatRangeStart(startValue, includeStartYear)} - ${formatRangeStart(endValue, true)}`;
};

const STATUS_ICONS = {
  failed: gameRed,
  active: gameBlue,
  completed: gameGreen,
};

const gameColumns = (onView) => [
  {
    key: "name",
    label: "Games",
    width: "2fr",
    render: (row) => {
      const status = row.progress?.status?.toLowerCase() || "active";
      const icon = STATUS_ICONS[status] || gameBlue;
      return (
        <span className="data-table__row-label">
          <img src={icon} alt="" style={{ width: 20, height: 20 }} />
          {row.name}
        </span>
      );
    },
  },
  { key: "status", label: "Status", width: "1fr", render: (row) => <StatusBadge status={row.progress?.status || "active"} /> },
  { key: "type", label: "Type", width: "1fr", render: (row) => row.type },
  { key: "points", label: "Points", width: "1fr", render: (row) => <PointsValue value={row.progress?.reward_points || 0} /> },
  { key: "date", label: "Date", width: "1fr", render: (row) => row.created_date || formatDate(row.created_at) },
  {
    key: "view",
    label: "",
    width: "48px",
    align: "center",
    render: (row) => (
      <button
        type="button"
        onClick={() => onView(row.id)}
        style={{ border: "none", background: "none", cursor: "pointer", padding: 4 }}
        aria-label={`View ${row.name}`}
      >
        <img src={eyeIcon} alt="" style={{ width: 18, height: 18 }} />
      </button>
    ),
  },
];

const GamesPage = ({ onViewGame }) => {
  document.title = "Games | Omaha Food Lovers";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gamesData, setGamesData] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [loadingMoreGames, setLoadingMoreGames] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreGames, setHasMoreGames] = useState(true);
  const [stats, setStats] = useState({
    active: 0,
    completed: 0,
    failed: 0,
    pointsEarned: 0,
  });

  // Filter states
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);

  // Configure toastr options on mount
  useEffect(() => {
    toastr.options = {
      positionClass: "toast-bottom-left",
      timeOut: 5000,
      closeButton: true,
      progressBar: true,
    };
  }, []);

  // Fetch stats from API
  const fetchStats = useCallback(async () => {
    try {
      const response = await get(endpoints.gamesStats);
      if (response.data.success) {
        const apiStats = response.data.data.stats;
        setStats({
          active: apiStats.active_games || 0,
          completed: apiStats.completed_games || 0,
          failed: apiStats.failed_games || 0,
          pointsEarned: apiStats.points_earned || 0,
        });
      }
    } catch (err) {
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    }
  }, []);

  // Fetch games from API with filters
  const fetchGames = useCallback(async (page = 1, isLoadMore = false) => {
    const setLoading = isLoadMore ? setLoadingMoreGames : setLoadingGames;
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", "16");

      // Add status parameter if not "all"
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }

      // Add type parameter if not "all"
      if (typeFilter !== "all") {
        params.append("type", typeFilter);
      }

      // Build the full URL with query parameters
      const url = `${endpoints.games}?${params.toString()}`;

      const response = await get(url);

      if (response.data.success) {
        const newGames = response.data.data.games || [];
        const pagination = response.data.data;

        if (isLoadMore) {
          setGamesData((prev) => [...prev, ...newGames]);
        } else {
          setGamesData(newGames);
        }

        setCurrentPage(pagination?.current_page || page);
        setHasMoreGames(pagination?.current_page < pagination?.last_page);
      }
    } catch (err) {
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      if (!isLoadMore) {
        setGamesData([]);
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  // Fetch games on mount
  useEffect(() => {
    fetchStats();
    fetchGames(1, false);
  }, [fetchStats, fetchGames]);

  // Infinite scroll handler
  const handleLoadMoreGames = useCallback(() => {
    if (hasMoreGames && !loadingMoreGames) {
      fetchGames(currentPage + 1, true);
    }
  }, [hasMoreGames, loadingMoreGames, currentPage, fetchGames]);

  const handleViewGame = async (gameId) => {
    const game = gamesData.find(g => g.id === gameId);
    if (game) {
      try {
        const response = await get(endpoints.game.replace('{{game_slug}}', game.slug));
        
        if (response.data.success) {
          const gameDetail = response.data.data.game;
          
          // Transform game detail data to modal format
          const now = new Date();
          const modalGameData = {
            icon: gameControllerIcon,
            statusLabel: gameDetail.progress?.status?.charAt(0).toUpperCase() + gameDetail.progress?.status?.slice(1) || "Active",
            title: gameDetail.name,
            description: gameDetail.description || `${gameDetail.type} game`,
            progressLabel: "Progress",
            progressValue: `${gameDetail.progress?.progress_value || 0}/${gameDetail.progress?.target_value || 0}`,
            gameType: gameDetail.type,
            weeks: gameDetail.type === 'streak' ? gameDetail.progress?.weekly_completion?.map((week) => {
              const weekStart = week.week_start_date || week.start;
              const weekEnd = week.week_end_date || week.end;
              const weekRange = formatWeekRange(weekStart, weekEnd);
              if (week.completed) {
                return { label: `Week ${week.week}`, range: weekRange, state: "done" };
              }
              // Check if week is failed (not completed and start date has passed)
              if (weekEnd) {
                const weekEndDate = new Date(weekEnd);
                if (now > weekEndDate) {
                  return { label: `Week ${week.week}`, range: weekRange, state: "failed" };
                }
              }
              return { label: `Week ${week.week}`, range: weekRange, state: "pending" };
            }) || [] : [],
            progressPercent: (gameDetail.progress?.target_value > 0
                ? Math.round((gameDetail.progress?.progress_value / gameDetail.progress?.target_value) * 100)
                : 0),
            history: gameDetail.type === 'visits'
              ? (gameDetail.progress?.visited_restaurants?.map((visit, index) => {
                  const formattedDate = visit.visited_at
                    ? new Date(visit.visited_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })
                    : "-";
                  const address = visit.address
                    ? `${visit.address.address || ''}, ${visit.address.city || ''}, ${visit.address.state || ''}`
                    : "Address not available";
                  // Calculate points per visit by dividing total reward points by target value (total visits)
                  const pointsPerVisit = gameDetail.progress?.target_value > 0
                    ? Math.round((gameDetail.progress?.reward_points || 0) / gameDetail.progress?.target_value)
                    : 0;
                  return {
                    week: index + 1,
                    status: "Completed",
                    tone: "green",
                    points: `${pointsPerVisit}pts`,
                    message: visit.title || "Restaurant visit",
                    date: formattedDate,
                    address: address
                  };
                }) || [])
              : gameDetail.type === 'streak' ? gameDetail.progress?.weekly_completion?.map((week) => {
                  let status = "Not started";
                  let tone = "grey";
                  let points = `${Number(week.points || 0).toLocaleString()} pts`;
                  let message = "";
                  const weekStart = week.week_start_date || week.start;
                  const weekEnd = week.week_end_date || week.end;
                  const weekRange = formatWeekRange(weekStart, weekEnd);
                  const transactionDate = formatDateTime(week.points_transaction_date);

                  if (week.completed) {
                    status = "Completed";
                    tone = "green";
                    message = "Great job!";
                  } else if (weekEnd) {
                    const weekEndDate = new Date(weekEnd);
                    if (now > weekEndDate) {
                      status = "Failed";
                      tone = "red";
                      message = "Your streak broke";
                    } else {
                      status = "In Progress";
                      tone = "blue";
                      message = "Keep going!";
                    }
                  }

                  return {
                    week: week.week,
                    weekRange,
                    status: status,
                    tone: tone,
                    points: points,
                    message: message,
                    date: transactionDate
                  };
                }) || [] : [],
            stats: [
              { label: "Type", value: gameDetail.type },
              { label: "Started", value: formatDate(gameDetail.progress?.started_at) },
              { label: "Status", value: gameDetail.progress?.status || "active" },
              { label: "Reward Points", value: `${gameDetail.progress?.reward_points || 0} pts` },
              { label: "Points Awarded", value: `${gameDetail.progress?.points_awarded || 0} pts` },
            ],
            footnote: gameDetail.description || "Complete the challenge to earn rewards",
          };
          setSelectedGame(modalGameData);
          setIsModalOpen(true);
        }
      } catch (err) {
        const errorMessage = extractErrorMessage(err);
        toastr.error(errorMessage);
      }
    }
    if (onViewGame) onViewGame(gameId);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedGame(null);
  };

  // Static game types for dropdown (based on API-supported types)
  const gameTypes = ["all", "streak", "visits"];

  // Status filter options
  const statusFilters = [
    { value: "all", label: "All Games" },
    { value: "active", label: "Active Games" },
    { value: "completed", label: "Completed Games" },
    { value: "failed", label: "Failed Games" },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="g-3" style={{ marginBottom: 24 }}>
          <Col xl={3} md={6}>
            <StatMiniCard icon={gameControllerIcon} tone="blue" label="Active Games" value={stats.active} />
          </Col>
          <Col xl={3} md={6}>
            <StatMiniCard icon={gameControllerIcon} tone="green" label="Completed Games" value={stats.completed} />
          </Col>
          <Col xl={3} md={6}>
            <StatMiniCard icon={gameControllerIcon} tone="red" label="Failed Games" value={stats.failed} />
          </Col>
          <Col xl={3} md={6}>
            <StatMiniCard icon={gameControllerIcon} tone="purple" label="Points Earned" value={stats.pointsEarned.toLocaleString()} />
          </Col>
        </Row>

        {/* Filter Controls */}
        <Row className="games-page-filters-row">
          <Col>
            <div className="games-page-filters">
              {/* Status Filter Pills - Left Side */}
              <div className="games-page-filters__pills">
                {statusFilters.map((filter) => (
                  <button
                    key={filter.value}
                    type="button"
                    onClick={() => setStatusFilter(filter.value)}
                    className={`games-page-filters__pill ${statusFilter === filter.value ? "games-page-filters__pill--active" : ""}`}
                    aria-label={`Filter by ${filter.label}`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {/* Type Filter Dropdown - Right Side */}
              <Dropdown isOpen={typeDropdownOpen} toggle={() => setTypeDropdownOpen(!typeDropdownOpen)}>
                <DropdownToggle tag="button" className="games-page-filters__dropdown-toggle" aria-label="Filter by game type">
                  Type: {typeFilter === "all" ? "All" : typeFilter}
                  <img src={chevronDownIcon} alt="" style={{ width: 16, height: 16, marginLeft: 8 }} />
                </DropdownToggle>
                <DropdownMenu className="explore-filter-dropdown">
                  {gameTypes.map((type) => (
                    <DropdownItem
                      key={type}
                      onClick={() => setTypeFilter(type)}
                      active={typeFilter === type}
                    >
                      {type === "all" ? "All" : type}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          </Col>
        </Row>

        <DataTable
          columns={gameColumns(handleViewGame)}
          rows={gamesData}
          getRowKey={(row) => row.id}
          loading={loadingGames}
          loadingMore={loadingMoreGames}
          onLoadMore={handleLoadMoreGames}
          hasMore={hasMoreGames}
          emptyState={!loadingGames && gamesData.length === 0 ? (
            <EmptyState
              title="No Games found"
              message="Try adjusting your filters to see more Games"
            />
          ) : null}
        />
      </Container>

      <GameActivityDetailModal
        game={selectedGame}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

GamesPage.propTypes = {
  onViewGame: PropTypes.func,
};

GamesPage.defaultProps = {
  onViewGame: () => {},
};

export default GamesPage;
